import { useCallback, useMemo } from "react";
import { useFormikContext } from "formik";
import { mixed, object, string, type ObjectSchema } from "yup";
import { Button, Flex, ModalV2 } from "@jsq/ds/core";
import { Form, FormSelect, FormTextInput, SubmitButton } from "@jsq/ds/forms";
import { SPACING } from "~/styles/variables";
import { buildDataPackageSections } from "../dataPackageSections";
import {
  NAME_NOT_UNIQUE_ERROR,
  useDataPackageNameValidator,
  type DataPackageNameValidator,
} from "../useDataPackageNameValidator";

export type DuplicateDataPackageFormValues = {
  sourceDataPackageId: number | null;
  name: string;
  description: string;
};

type DataPackageOption = {
  id: number;
  name: string;
  arenaId?: number | null;
};

const MIN_LENGTH = 3;
const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 200;

const createValidationSchema = (
  validateName: DataPackageNameValidator,
): ObjectSchema<DuplicateDataPackageFormValues> =>
  object({
    sourceDataPackageId: mixed<number>()
      .required("Please select a data package")
      .transform((v) => (v === "" || v == null ? undefined : Number(v)))
      .test(
        "valid-id",
        "Please select a data package",
        (v) => typeof v === "number" && !Number.isNaN(v),
      ),
    name: string()
      .required("Name this data package")
      .trim()
      .min(MIN_LENGTH, `Must be at least ${MIN_LENGTH} characters`)
      .max(NAME_MAX_LENGTH, `Maximum ${NAME_MAX_LENGTH} characters`)
      .test("unique-name", NAME_NOT_UNIQUE_ERROR, async function testUnique(value) {
        if (!value) return true;
        const error = await validateName(value);
        if (!error) return true;
        return this.createError({ message: error });
      }),
    description: string()
      .trim()
      .test(
        "min-if-provided",
        `Must be at least ${MIN_LENGTH} characters`,
        (value) => !value || value.length >= MIN_LENGTH,
      )
      .max(DESCRIPTION_MAX_LENGTH, `Maximum ${DESCRIPTION_MAX_LENGTH} characters`)
      .default("")
      .defined(),
  });

const SaveDataPackageButton = ({ isCreating }: { isCreating: boolean }) => {
  const { values, errors } = useFormikContext<DuplicateDataPackageFormValues>();
  const isNameTooShort = values.name.trim().length < MIN_LENGTH;
  const hasNoSource = values.sourceDataPackageId == null;
  const hasErrors = Object.keys(errors).length > 0;
  return (
    <SubmitButton
      isLoading={isCreating}
      disabled={isNameTooShort || hasNoSource || hasErrors}
      data-testid="create-data-package-button"
    >
      Save data package
    </SubmitButton>
  );
};

export type DuplicateDataPackageModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (values: DuplicateDataPackageFormValues) => Promise<void>;
  dataPackages: DataPackageOption[];
  arenaId: number | null;
  arenaName?: string;
  isCreating: boolean;
  defaultSourceDataPackageId?: number | null;
};

export const DuplicateDataPackageModal = ({
  isOpen,
  onClose,
  onCreate,
  dataPackages,
  arenaId,
  arenaName,
  isCreating,
  defaultSourceDataPackageId,
}: DuplicateDataPackageModalProps) => {
  const packageSections = buildDataPackageSections(dataPackages, arenaName);

  const initialValues: DuplicateDataPackageFormValues = useMemo(
    () => ({
      sourceDataPackageId: defaultSourceDataPackageId ?? null,
      name: "",
      description: "",
    }),
    [defaultSourceDataPackageId],
  );

  const handleSubmit = useCallback(
    async (values: DuplicateDataPackageFormValues) => {
      await onCreate(values);
    },
    [onCreate],
  );

  const validateName = useDataPackageNameValidator({ arenaId });
  const validationSchema = useMemo(() => createValidationSchema(validateName), [validateName]);

  return (
    <ModalV2
      show={isOpen}
      onHide={onClose}
      title="Data package settings"
      data-testid="duplicate-data-package-modal"
    >
      <Form<DuplicateDataPackageFormValues>
        initialValues={initialValues}
        yupValidationSchema={validationSchema}
        onSubmit={handleSubmit}
        submitOnlyChangedValues={false}
      >
        <ModalV2.Body>
          <Flex flexDirection="column" gap={SPACING.LARGE}>
            <FormSelect
              name="sourceDataPackageId"
              label="Duplicate from"
              placeholder="Select an existing data package"
              data-testid="source-data-package-select"
              full
            >
              {packageSections.map((section) => (
                <FormSelect.Section key={section.key} name={section.name}>
                  {section.packages.map((pkg) => (
                    <FormSelect.Option key={pkg.id} value={pkg.id}>
                      {pkg.name}
                    </FormSelect.Option>
                  ))}
                </FormSelect.Section>
              ))}
            </FormSelect>

            <FormTextInput
              name="name"
              label="New data package name"
              data-testid="new-data-package-name"
              isFullWidth
            />

            <FormTextInput
              name="description"
              label="Description"
              data-testid="new-data-package-description"
              isFullWidth
            />
          </Flex>
        </ModalV2.Body>

        <ModalV2.Footer>
          <Flex justifyContent="flex-end" gap={SPACING.SMALL}>
            <Button variant="neutral" onClick={onClose}>
              Cancel
            </Button>
            <SaveDataPackageButton isCreating={isCreating} />
          </Flex>
        </ModalV2.Footer>
      </Form>
    </ModalV2>
  );
};
